import { useQueryClient } from '@tanstack/react-query';
import { getApiV1ExamCorrectionGridExampublishidQueryOptions } from '@/shared/api/generated';

/**
 * Tamanho de página usado para varrer o subgrid de correção em busca dos
 * pendentes — alto o bastante para resolver a maioria das questões em 1–2
 * requisições, já que o subgrid não tem um filtro dedicado "somente pendentes".
 */
const PENDING_SCAN_PAGE_SIZE = 200;

/**
 * Hook para levantar todos os `answerId` pendentes (sem `grade`) de uma questão,
 * percorrendo as páginas do subgrid de correção.
 *
 * @returns `collectPendingAnswerIds`, que resolve para o array de `answerId` pendentes da questão
 *
 * @example
 * ```ts
 * const { collectPendingAnswerIds } = usePendingAnswerIds();
 * const answerIds = await collectPendingAnswerIds(examPublishId, examItemId);
 * ```
 */
export function usePendingAnswerIds() {
  const queryClient = useQueryClient();

  async function collectPendingAnswerIds(
    examPublishId: number,
    examItemId: number,
  ): Promise<number[]> {
    const pendingIds: number[] = [];
    let pageNumber = 1;
    let totalPages = 1;

    do {
      const page = await queryClient.fetchQuery(
        getApiV1ExamCorrectionGridExampublishidQueryOptions(examPublishId, {
          examItemId,
          pageNumber,
          pageSize: PENDING_SCAN_PAGE_SIZE,
        }),
      );

      for (const row of page?.data ?? []) {
        if (row.id !== undefined && (row.grade === null || row.grade === undefined)) {
          pendingIds.push(row.id);
        }
      }

      totalPages = page?.totalPages ?? 1;
      pageNumber += 1;
    } while (pageNumber <= totalPages);

    return pendingIds;
  }

  return { collectPendingAnswerIds } as const;
}
